import { useCheckIsAppealsExistQuery } from 'feature/appeals/redux/appeals.api'
import { useIsAuth } from 'feature/auth/hooks'
import { ComponentType, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useActions } from 'store/hooks'
import { Step, StepLabel, Stepper } from 'ui'
import { WarningModal } from 'widget/WarningModal/WarningModal'

import { orderFormSpace } from '../namespaces/orderFormSpace'

import style from './CreateOrder.module.scss'

type StepComponent = ComponentType<{
  completeStep: (isLastStep: boolean) => void
  handlePrevious: (isFirstStep: boolean) => void
}>

type StepArrayItem = {
  StepView: StepComponent
  label: string
  id: string
}

type CreateOrderProps = {
  stepArray: StepArrayItem[]
  handleSubmit: () => void
  orderType: orderFormSpace.FormType
}

// формирование заявки
export function CreateOrder({
  stepArray,
  handleSubmit,
  orderType,
}: CreateOrderProps) {
  const { setActiveStepOrder } = useActions()
  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const isAuth = useIsAuth()
  const orderTypeValue = orderFormSpace.OrderType[orderType]
  const { data: appealsList, isLoading } = useCheckIsAppealsExistQuery(
    undefined,
    {
      skip: !isAuth,
    },
  )
  const activeStep = useAppSelector(
    state => state.orderForm.forms[orderType].activeStep,
  )
  const navigate = useNavigate()

  // Максимальный шаг, которого достиг пользователь.
  // Инициализируется из activeStep (восстановлен из localStorage),
  // обновляется при каждом переходе вперёд.
  const [maxReachedStep, setMaxReachedStep] = useState(activeStep)

  const handleNext = (isLastStep: boolean) => {
    if (isLastStep) {
      handleSubmit()
      return
    }
    const nextStep = activeStep + 1
    setActiveStepOrder(nextStep)
    if (nextStep > maxReachedStep) {
      setMaxReachedStep(nextStep)
    }
  }

  const handlePrevious = (isFirstStep: boolean) => {
    if (isFirstStep) {
      navigate('/')
      return
    }
    setActiveStepOrder(activeStep - 1)
  }
  const { StepView } = stepArray[activeStep]

  const onStepClick = (step: StepArrayItem) => {
    const clickedStep = stepArray.findIndex(item => item.id === step.id)
    // Разрешаем переход на любой шаг до maxReachedStep включительно
    if (clickedStep <= maxReachedStep && clickedStep !== activeStep) {
      setActiveStepOrder(clickedStep)
    }
  }

  const hideModal = () => {
    setModalOpen(false)
  }

  const isOrderExist = (type: orderFormSpace.OrderType): boolean => {
    return !!appealsList?.filter(order => order.type === type)?.length
  }

  useEffect(() => {
    if (!appealsList?.length && isLoading) return

    if (isOrderExist(orderTypeValue)) {
      setTimeout(() => {
        setModalOpen(true)
      }, 2000)
    }
  }, [appealsList, orderTypeValue])

  return (
    <section className={style.createOrder}>
      <Stepper activeStep={activeStep} className={style.stepper}>
        {stepArray.map((step, index) => {
          const isClickable = index <= maxReachedStep && index !== activeStep
          return (
            <Step
              key={step.id}
              className={activeStep > index ? style.hoverStep : ''}
            >
              <StepLabel
                onClick={() => onStepClick(step)}
                style={{ cursor: isClickable ? 'pointer' : 'default' }}
              >
                {step.label}
              </StepLabel>
            </Step>
          )
        })}
      </Stepper>
      <StepView completeStep={handleNext} handlePrevious={handlePrevious} />
      {isOrderExist(orderTypeValue) && (
        <WarningModal
          modalText={`Обратите внимание, что у вас уже есть активные заявки. 
        Если они не актуальные, пожалуйста, свяжитесь с нами, чтобы отменить их`}
          buttonText='Ок'
          isModalOpen={isModalOpen}
          handlerClick={hideModal}
          className={style.deleteModal}
        />
      )}
    </section>
  )
}
